// ═══════════════════════════════════════════════════════════════════════════════
//  src/advanced/salaTextos.js
//  LOS TEXTOS DE ALTO RENDIMIENTO · español e inglés
//
//  La costura que quedaba: La Situación ya hablaba el idioma del alumno, pero se
//  entraba a ella por una tarjeta en español. Un alumno en inglés veía media
//  pantalla en cada idioma, que es peor que no traducir nada — parece descuido,
//  no decisión.
//
//  ── POR QUÉ LAS TARJETAS VIVEN AQUÍ Y NO EN `advancedBank.js` ───────────────
//  Porque ese fichero es el BANCO: los 48 reactivos con su clave de respuesta,
//  que están en inglés porque son el material de estudio y no se traducen nunca.
//  Meterle una columna de idioma para el título de la tarjeta mezclaría dos
//  cosas que envejecen distinto.
//
//  Aquí sólo está lo que el alumno LEE sobre cada formato: cómo se llama, qué
//  promete y qué entrena. El identificador manda: `advancedBank.js` sigue siendo
//  la lista de formatos, y esto sólo le pone palabras.
//
//  ── QUIÉN DECIDE EL IDIOMA ──────────────────────────────────────────────────
//  `GET /api/v3/advanced/idioma`, y nadie más. Una sola fuente para las siete
//  tarjetas y para el formato que se abra: con dos reglas, el día que no
//  coincidan el alumno lee media sala en cada idioma.
// ═══════════════════════════════════════════════════════════════════════════════

const es = {
  tag: '🏔️ Nivel B2 en adelante',
  h1a: 'ALTO',
  h1b: 'RENDIMIENTO',
  lead1: 'A partir de B2 el problema deja de ser la gramática. Es sonar grosero '
    + 'sin querer, no captar el sarcasmo, decir',
  lead2: 'donde un nativo dice',
  lead3: '. Siete entrenamientos para exactamente eso — y el último no te '
    + 'califica el inglés: te dice si conseguiste lo que ibas a conseguir.',
  sinConexion: 'Cuatro de los siete formatos funcionan sin conexión y sin '
    + 'consumir IA. Sostén tu postura, El correo que decide y La situación sí '
    + 'necesitan servidor, porque ahí no hay respuesta correcta que comparar.',
  volver: 'Volver al gimnasio',

  // Pies de tarjeta
  reactivos: (n) => `${n} reactivos`,
  conIA: 'con IA',
  configurable: 'configurable',

  // Los acentos que de verdad tiene ESTE aparato. `n` es cuántos hay y `lista`
  // sus nombres, porque prometer cuatro cuando hay uno es vender humo.
  acentosVarios: (n, lista) => `Oído nativo puede usar ${n} acentos en este `
    + `dispositivo: ${lista}.`,
  acentosPocos: (n, lista) => `Tu sistema sólo tiene ${n === 1 ? 'una voz inglesa' : `${n} voces inglesas`}`
    + ` (${lista}), así que Oído nativo no podrá variar el acento tanto como debería.`,
  acentosNinguno: 'Oído nativo usa las voces del sistema: en Chrome o Edge '
    + 'tendrás más acentos disponibles.',

  // ── Las siete tarjetas ────────────────────────────────────────────────────
  formatos: {
    tone: {
      name: 'Tone Surgery',
      claim: 'La misma idea en tres temperaturas. Solo una cabe en esa sala.',
      trains: 'Registro y diplomacia',
    },
    subtext: {
      name: 'Subtexto',
      claim: 'Dice A y quiere decir B. ¿Qué te está diciendo realmente?',
      trains: 'Intención e implicatura',
    },
    lexis: {
      name: 'Precisión léxica',
      claim: 'De big problem a pressing issue. La palabra que va con esa otra.',
      trains: 'Colocaciones y matiz',
    },
    ear: {
      name: 'Oído nativo',
      claim: 'Velocidad real y acentos: británico, americano, australiano, indio.',
      trains: 'Escucha en condiciones reales',
    },
    debate: {
      name: 'Sostén tu postura',
      claim: 'Dos minutos y medio de argumento continuo. A mitad, te interrumpen.',
      trains: 'Producción larga',
    },
    email: {
      name: 'El correo que decide',
      claim: 'Reescribe un correo real: más corto, más firme, sin ofender.',
      trains: 'Escritura profesional',
    },
    situacion: {
      name: 'La situación',
      claim: 'Negocia, discute o da una mala noticia con alguien que no te lo '
        + 'pone fácil. Al final: ¿lo conseguiste?',
      trains: 'Conseguir cosas hablando',
    },
  },

  // ── DENTRO DE LAS CUATRO PRUEBAS ──────────────────────────────────────────
  drill: {
    salir: '← Salir',
    laSituacion: 'La situación',
    completa: 'Completa la frase',
    escucharTono: '🔊 Escuchar el tono',
    escuchar: 'Escuchar',
    masDespacio: 'Más despacio',
    velocidadReal: 'velocidad real',
    vozSuplente: (acento) => `Tu sistema no tiene voz ${acento}; se usó otra voz `
      + 'inglesa. El acento que oyes no es el del reactivo.',
    correcto: '✓ Correcto. ',
    incorrecto: '✕ ',
    siguiente: 'Siguiente →',
    verResultado: 'Ver resultado →',
    // Resultado
    aciertos: (pct) => `${pct}% de aciertos`,
    correctas: 'correctas',
    falladas: 'falladas',
    minutos: 'minutos',
    cierreBueno: 'Estás leyendo el matiz, no sólo las palabras. Ese es '
      + 'exactamente el salto de B2 a C1.',
    cierreFlojo: 'Lo importante aquí no es el marcador: es que ahora sabes por '
      + 'qué fallaban las otras dos opciones.',
    otraRonda: 'Otra ronda →',
    volverSala: 'Volver a la sala',
  },

  // ── DENTRO DEL DEBATE ─────────────────────────────────────────────────────
  debate: {
    laMocion: 'La moción',
    defiendes: '▲ Defiendes la moción',
    atacas: '▼ La atacas',
    aviso: 'La postura te tocó por sorteo, estés de acuerdo o no — ahí está el '
      + 'entrenamiento. Habla sin parar dos minutos y medio. A mitad de camino '
      + 'te van a interrumpir; recuperar el hilo es la parte que cuenta.',
    empezar: '🎤 Empezar a hablar',
    otraMocion: 'Otra moción',
    otraMocionFlecha: 'Otra moción →',
    objetivo: (m, s) => `Objetivo: ${m}:${s} más`,
    objetivoHecho: 'Objetivo cumplido — puedes cerrar cuando quieras',
    contraataque: 'Contraataque',
    cerrar: '■ Cerrar mi argumento',
    leyendo: 'Leyendo tu argumento…',
    sinCalificar: 'Sin calificar',
    sostenido: 'sostenido',
    palabras: 'palabras',
    ritmo: 'palabras/min',
    sinIA: 'No pudimos evaluarlo con IA en este momento, así que no te damos '
      + 'una nota inventada.',
    sinMicro: 'Este formato necesita reconocimiento de voz. Usa Chrome o Edge.',
    // El contraataque de respaldo, cuando el proveedor no contesta. Va en los
    // dos idiomas porque el alumno lo LEE bajo la frase en inglés.
    contraRespaldoEs: 'Sigue — quiero oír cómo cierras ese argumento.',
  },

  // ── DENTRO DEL CORREO ─────────────────────────────────────────────────────
  email: {
    bandeja: 'Llegó a tu bandeja',
    escribeAqui: 'Escribe tu respuesta en inglés…',
    objetivoPalabras: (lo, hi) => `objetivo ${lo}–${hi}`,
    enviar: 'Enviar a revisión →',
    masLineas: 'Escribe unas líneas más',
    palabrasMedido: 'palabras · medido',
    registroJuzgado: 'registro · juzgado',
    rangoPedido: 'rango pedido',
    revisando: 'Revisando tu correo…',
    otroEncargo: 'Otro encargo',
    palabras: 'palabras',
    objetivo: 'objetivo',
    sinCalificar: 'Sin calificar',
    limite: 'El límite es parte del encargo: lo que sobra en un correo real no '
      + 'se lee. Puedes enviarlo fuera de rango — sólo te va a costar puntos.',
    sinIA: 'Tu correo quedó guardado, pero no pudimos evaluar el registro ahora '
      + 'mismo — y no vamos a inventarte una nota.',
    reescribir: 'Reescribirlo con esto en mente →',
  },
};

const en = {
  tag: '🏔️ B2 and above',
  h1a: 'HIGH',
  h1b: 'PERFORMANCE',
  lead1: 'From B2 on, grammar stops being the problem. The problem is sounding '
    + 'rude without meaning to, missing the sarcasm, saying',
  lead2: 'where a native says',
  lead3: '. Seven drills for exactly that — and the last one does not grade '
    + 'your English: it tells you whether you got what you went in for.',
  sinConexion: 'Four of the seven work offline and burn no AI. Hold your '
    + 'ground, The email that decides and The situation do need the server, '
    + 'because there is no right answer to compare against.',
  volver: 'Back to the gym',

  reactivos: (n) => `${n} items`,
  conIA: 'with AI',
  configurable: 'configurable',

  acentosVarios: (n, lista) => `Native ear can use ${n} accents on this device: `
    + `${lista}.`,
  acentosPocos: (n, lista) => `Your system only has ${n === 1 ? 'one English voice' : `${n} English voices`}`
    + ` (${lista}), so Native ear cannot vary the accent as much as it should.`,
  acentosNinguno: 'Native ear uses your system voices: on Chrome or Edge you '
    + 'will have more accents available.',

  formatos: {
    tone: {
      // Se queda igual: ya es inglés y es el nombre del formato.
      name: 'Tone Surgery',
      claim: 'The same point at three temperatures. Only one fits that room.',
      trains: 'Register and diplomacy',
    },
    subtext: {
      name: 'Subtext',
      claim: 'They say A and mean B. What are they actually telling you?',
      trains: 'Intent and implicature',
    },
    lexis: {
      name: 'Lexical precision',
      claim: 'From big problem to pressing issue. The word that goes with that word.',
      trains: 'Collocation and nuance',
    },
    ear: {
      name: 'Native ear',
      claim: 'Real speed and real accents: British, American, Australian, Indian.',
      trains: 'Listening in real conditions',
    },
    debate: {
      name: 'Hold your ground',
      claim: 'Two and a half minutes of unbroken argument. Halfway through, you get cut off.',
      trains: 'Sustained production',
    },
    email: {
      name: 'The email that decides',
      claim: 'Rewrite a real email: shorter, firmer, without giving offence.',
      trains: 'Professional writing',
    },
    situacion: {
      name: 'The situation',
      claim: 'Negotiate, take a complaint or break bad news to someone who will '
        + 'not make it easy. At the end: did you pull it off?',
      trains: 'Getting things done by talking',
    },
  },

  drill: {
    salir: '← Leave',
    laSituacion: 'The situation',
    completa: 'Complete the sentence',
    escucharTono: '🔊 Hear the tone',
    escuchar: 'Listen',
    masDespacio: 'Slower',
    velocidadReal: 'real speed',
    vozSuplente: (acento) => `Your system has no ${acento} voice; another English `
      + 'voice was used. The accent you hear is not the one in the item.',
    correcto: '✓ Right. ',
    incorrecto: '✕ ',
    siguiente: 'Next →',
    verResultado: 'See result →',
    aciertos: (pct) => `${pct}% correct`,
    correctas: 'right',
    falladas: 'wrong',
    minutos: 'minutes',
    cierreBueno: 'You are reading the nuance, not just the words. That is exactly '
      + 'the jump from B2 to C1.',
    cierreFlojo: 'The score is not the point here: the point is that you now know '
      + 'why the other two options failed.',
    otraRonda: 'Another round →',
    volverSala: 'Back to the room',
  },

  debate: {
    laMocion: 'The motion',
    defiendes: '▲ You defend the motion',
    atacas: '▼ You attack it',
    aviso: 'You were assigned this side at random, whether you agree or not — '
      + 'that is the training. Talk without stopping for two and a half minutes. '
      + 'Halfway through you will be interrupted; picking the thread back up is '
      + 'the part that counts.',
    empezar: '🎤 Start talking',
    otraMocion: 'Another motion',
    otraMocionFlecha: 'Another motion →',
    objetivo: (m, s) => `Target: ${m}:${s} to go`,
    objetivoHecho: 'Target met — close whenever you want',
    contraataque: 'Counterpunch',
    cerrar: '■ Close my argument',
    leyendo: 'Reading your argument…',
    sinCalificar: 'Not graded',
    sostenido: 'sustained',
    palabras: 'words',
    ritmo: 'words/min',
    sinIA: 'We could not evaluate it with AI right now, so we are not giving you '
      + 'a made-up score.',
    sinMicro: 'This format needs speech recognition. Use Chrome or Edge.',
    contraRespaldoEs: 'Keep going — I want to hear how you close that argument.',
  },

  email: {
    bandeja: 'It landed in your inbox',
    escribeAqui: 'Write your reply in English…',
    objetivoPalabras: (lo, hi) => `target ${lo}–${hi}`,
    enviar: 'Send for review →',
    masLineas: 'Write a few more lines',
    palabrasMedido: 'words · measured',
    registroJuzgado: 'register · judged',
    rangoPedido: 'range asked for',
    revisando: 'Reviewing your email…',
    otroEncargo: 'Another brief',
    palabras: 'words',
    objetivo: 'target',
    sinCalificar: 'Not graded',
    limite: 'The limit is part of the brief: in a real email, what is extra does '
      + 'not get read. You can send it out of range — it will just cost you points.',
    sinIA: 'Your email was saved, but we could not judge the register right now '
      + '— and we are not going to invent a score for you.',
    reescribir: 'Rewrite it with this in mind →',
  },
};

/** Los textos del idioma efectivo. Cualquier cosa que no sea "en" cae en
 *  español, que es el punto de partida de toda la app. */
export function textosSala(idiomaEfectivo) {
  return idiomaEfectivo === 'en' ? en : es;
}

/** Las dos columnas, para poder compararlas sin montar la pantalla. */
export const columnasSala = { es, en };
